import { useEffect, useCallback, createElement } from "react";
import { toast } from "sonner";
import { requestNotificationPermission, onForegroundMessage } from "@/lib/firebase";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
const API_URL = import.meta.env.VITE_API_URL as string;

/**
 * Registers the FCM token with the backend so the server can push to this device.
 * Silently no-ops if the user is not authenticated.
 */
async function registerTokenWithBackend(token: string): Promise<void> {
  const authToken =
    typeof window !== "undefined"
      ? localStorage.getItem("token") || localStorage.getItem("accessToken")
      : null;
  if (!authToken) return; // unauthenticated – skip

  try {
    const res = await fetch(`${API_URL}/users/fcm-token`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ fcmToken: token }),
    });
    if (res.status === 401) {
      console.warn("[Notifications] 401 Unauthorized registering FCM token");
      return;
    }
    console.log("[Notifications] FCM token registered with backend ✓");
  } catch (err) {
    console.warn("[Notifications] Failed to register FCM token with backend:", err);
  }
}

export async function registerFcmToken(): Promise<void> {
  const token = await requestNotificationPermission(VAPID_KEY);
  if (token) {
    await registerTokenWithBackend(token);
  }
}

/**
 * useNotifications
 *
 * - On mount, requests push-notification permission and sends the FCM token to
 *   the backend so the server can target this device.
 * - Wires up a foreground message listener that shows an in-app toast banner.
 * - Background / closed-app notifications are handled by the service worker.
 */
export function useNotifications() {
  const init = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (!("serviceWorker" in navigator)) return;

    const token = await requestNotificationPermission(VAPID_KEY);
    if (token) {
      await registerTokenWithBackend(token);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  // Foreground message listener – show toast while the app is open
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      // Vibrate and play a short sound for foreground notifications
      if (typeof window !== "undefined") {
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);

        const audio = new Audio("/sounds/notify.mp3");
        audio.play().catch(() => {
          /* autoplay may be blocked until user interacts */
        });
      }

      // Support both notification-type and data-only payloads
      const title =
        payload.notification?.title ?? payload.data?.title ?? "New notification";
      const body = payload.notification?.body ?? payload.data?.body;
      const url = payload.data?.url;
      const iconUrl = payload.data?.icon;

      toast(title, {
        description: body,
        duration: 6000,
        icon: iconUrl
          ? createElement("img", {
              src: iconUrl,
              alt: "",
              width: 60,
              height: 60,
              style: { borderRadius: "9999px", objectFit: "cover" },
            })
          : "🔔",
        action: url
          ? { label: "View", onClick: () => (window.location.href = url) }
          : undefined,
      });
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);
}
